/**
 * TMS Dispatch Board — Jerry's Dispatch Optimization Panel
 * Spec: _spec_trucking_a1_v1.md § 5 Dispatch Optimization Agent
 */

import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useRenderMode } from '@/hooks/useRenderMode';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Truck, MapPin, User, Clock, RefreshCcw,
  ChevronRight, Zap, Bot, CheckCircle2, Package, Loader2, UserCheck, X,
} from 'lucide-react';
import { api } from '@/lib/api';
import { flattenEntityItems, flattenEntityRow, str } from '@/lib/fms-utils';
import { DataErrorBanner, type DataErrorDetail } from '@/components/ui/data-error-banner';
import { captureApiError } from '@/lib/api-error';

// ── Types ─────────────────────────────────────────────────────────────────────

interface DriverJob {
  id: string; type: string; container: string; size: string;
  status: string; pickup: string; delivery: string; time: string;
}
interface Driver {
  id: string; name: string; plate: string; status: string;
  current_job: string | null; eta_free: string; total_jobs: number;
  location: string; phone: string; jobs: DriverJob[];
}
interface UnassignedJob {
  id: string; rawId: string; type: string; container: string; size: string;
  customer: string; port: string; delivery: string; lfd: string;
  priority: string; note: string;
}
interface StreetTurn {
  job_a: string; job_b: string; driver: string;
  match_score: number; savings: string; note: string;
}

const statusColors: Record<string, string> = {
  available:  'bg-green-100 text-green-700',
  on_job:     'bg-blue-100 text-blue-700',
  at_port:    'bg-orange-100 text-orange-700',
  collecting: 'bg-purple-100 text-purple-700',
  returning:  'bg-yellow-100 text-yellow-700',
  off_duty:   'bg-gray-100 text-gray-600',
};

const priorityConfig: Record<string, string> = {
  high:   'border-red-300 bg-red-50',
  medium: 'border-orange-200 bg-orange-50/50',
  low:    'border-gray-200',
};

const HMS_JOB_TYPES = new Set(['IMP','EXP','RET','COL','IMP+RET','COL+EXP','T/T','REPO','STR']);
const TMS_JOB_TYPES = new Set(['TPT','LCL','FCL']);

// ── Component ─────────────────────────────────────────────────────────────────

export default function TmsDispatchPage() {
  const isMobile = useRenderMode() === 'mobile';
  const location = useLocation();
  const isHmsModule = location.pathname.startsWith('/hms/');
  const [dateFilter, setDateFilter] = useState('today');
  const [loading, setLoading] = useState(true);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [unassignedJobs, setUnassignedJobs] = useState<UnassignedJob[]>([]);
  const [streetTurns, setStreetTurns] = useState<StreetTurn[]>([]);
  const [apiError, setApiError] = useState<DataErrorDetail | null>(null);
  // Assign driver state
  const [assignJobId, setAssignJobId] = useState<string | null>(null);
  const [assignJobRawId, setAssignJobRawId] = useState<string | null>(null);
  const [assignDriver, setAssignDriver] = useState<string>('');
  const [assigning, setAssigning] = useState(false);
  const [autoDispatching, setAutoDispatching] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch job-trips for driver data
      const tripsRes = await api.get<{ items?: any[] }>('/job-trips?limit=100');
      const trips = flattenEntityItems(tripsRes);

      // Group trips by driver — filter to module types first
      const moduleTypes = isHmsModule ? HMS_JOB_TYPES : TMS_JOB_TYPES;
      const filteredTrips = trips.filter((t) => {
        const jt = (str(t, 'job_type', 'jobType') || '').toUpperCase();
        return moduleTypes.has(jt);
      });
      const driverMap = new Map<string, Driver>();
      for (const t of filteredTrips) {
        const dName = str(t, 'driverName', 'driver_name', 'driver');
        if (!dName) continue;
        const dId = str(t, 'driverId', 'driver_id') || dName;
        if (!driverMap.has(dId)) {
          driverMap.set(dId, {
            id: dId,
            name: dName,
            plate: str(t, 'vehicle_no', 'vehiclePlate', 'vehicle_plate', 'plate', 'vehicle'),
            status: 'available',
            current_job: null,
            eta_free: str(t, 'etaFree', 'eta_free') || '—',
            total_jobs: 0,
            location: str(t, 'location', 'currentLocation', 'current_location'),
            phone: str(t, 'driverPhone', 'driver_phone', 'phone'),
            jobs: [],
          });
        }
        const drv = driverMap.get(dId)!;
        drv.total_jobs += 1;
        drv.current_job = str(t, 'job_number', 'jobNumber', 'trip_no', 'id');
        drv.jobs.push({
          id: str(t, 'job_number', 'jobNumber', 'trip_no', 'id'),
          type: str(t, 'job_type', 'jobType', 'trip_type', 'type'),
          container: str(t, 'containerNo', 'container_no'),
          size: str(t, 'containerSize', 'container_size'),
          status: str(t, 'status'),
          pickup: str(t, 'pickupLocation', 'pickup_location', 'origin'),
          delivery: str(t, 'deliveryLocation', 'delivery_location', 'destination'),
          time: str(t, 'scheduledTime', 'scheduled_time'),
        });
      }
      // Derive driver status from accumulated trip statuses
      for (const drv of driverMap.values()) {
        if (drv.jobs.some((j) => j.status === 'in_progress')) {
          drv.status = 'on_job';
        }
      }
      setDrivers(Array.from(driverMap.values()));

      // Fetch unassigned jobs
      const jobsRes = await api.get<{ items?: any[] }>('/jobs?limit=50');
      const allJobs = flattenEntityItems(jobsRes);
      setUnassignedJobs(allJobs
        .filter((j) => {
          const noDriver = !j.driverName && !j.driver_name && !j.driver;
          const correctType = moduleTypes.has((str(j, 'job_type', 'jobType', 'type') || '').toUpperCase());
          return noDriver && correctType;
        })
        .slice(0, 20)
        .map((j) => ({
          id: str(j, 'job_number', 'jobNumber', 'job_id', 'id'),
          rawId: str(j, 'id'),
          type: str(j, 'type', 'jobType', 'job_type'),
          container: str(j, 'containerNo', 'container_no'),
          size: str(j, 'containerSize', 'container_size'),
          customer: str(j, 'customer', 'customerName', 'customer_name'),
          port: str(j, 'port', 'pickupLocation', 'pickup_location'),
          delivery: str(j, 'deliveryAddress', 'delivery_address', 'destination'),
          lfd: str(j, 'lfd', 'lfdContainer', 'lfd_container'),
          priority: str(j, 'priority') || 'medium',
          note: str(j, 'note', 'notes'),
        })),
      );

      // Street turns would come from AI analysis — empty for now
      setStreetTurns([]);
    } catch (err) {
      setApiError(captureApiError(err, '/job-trips'));
      setDrivers([]);
      setUnassignedJobs([]);
      setStreetTurns([]);
    } finally {
      setLoading(false);
    }
  }, [isHmsModule]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const doAssignDriver = useCallback(async () => {
    if (!assignJobRawId || !assignDriver) return;
    setAssigning(true);
    try {
      const drv = drivers.find((d) => d.name === assignDriver);
      await api.patch(`/jobs/${assignJobRawId}`, { driver_name: assignDriver, vehicle_no: drv?.plate ?? '' });
      setAssignJobId(null);
      setAssignJobRawId(null);
      setAssignDriver('');
      await fetchData();
    } catch { /* silent */ } finally { setAssigning(false); }
  }, [assignJobRawId, assignDriver, drivers, fetchData]);

  const doAutoDispatch = useCallback(async () => {
    const available = drivers.filter((d) => d.status === 'available');
    const toAssign = unassignedJobs.slice(0, available.length);
    if (toAssign.length === 0) return;
    setAutoDispatching(true);
    try {
      await Promise.all(toAssign.map((job, i) =>
        api.patch(`/jobs/${job.rawId || job.id}`, { driver_name: available[i]?.name, vehicle_no: available[i]?.plate }),
      ));
      await fetchData();
    } catch { /* silent */ } finally { setAutoDispatching(false); }
  }, [drivers, unassignedJobs, fetchData]);

  const availableCount = drivers.filter((d) => d.status === 'available').length;
  const onJobCount = drivers.filter((d) => d.status !== 'available' && d.status !== 'off_duty').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isMobile) {
    return (
      <div className="space-y-3">
        {apiError && <DataErrorBanner error={apiError} entityLabel="调度面板 (Dispatch)" onRetry={fetchData} compact />}
        {/* Stats */}
        <div className="grid grid-cols-2 gap-2">
          <Card><CardContent className="p-3">
            <div className="text-[10px] text-muted-foreground">Available</div>
            <div className="text-2xl font-bold text-green-600">{availableCount}</div>
          </CardContent></Card>
          <Card><CardContent className="p-3">
            <div className="text-[10px] text-muted-foreground">On Job</div>
            <div className="text-2xl font-bold text-primary">{onJobCount}</div>
          </CardContent></Card>
        </div>
        {/* Driver cards */}
        <div className="space-y-2">
          {drivers.map((d) => (
            <Card key={d.id} className="overflow-hidden">
              <CardContent className="p-3">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-semibold text-sm">{d.name}</div>
                    <div className="text-xs font-mono text-muted-foreground">{d.plate}</div>
                  </div>
                  <Badge variant="outline" className={`text-[9px] shrink-0 ${
                    d.status === 'available' ? 'bg-green-50 text-green-700' :
                    d.status === 'off_duty' ? 'bg-gray-100 text-gray-600' : 'bg-blue-50 text-blue-700'
                  } capitalize`}>{d.status.replace('_', ' ')}</Badge>
                </div>
                <div className="mt-1.5 flex items-center gap-3 text-[10px] text-muted-foreground">
                  <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{d.location}</span>
                  <span className="flex items-center gap-1"><Clock className="h-3 w-3" />Free {d.eta_free}</span>
                </div>
                {d.jobs.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {d.jobs.map((j) => (
                      <div key={j.id} className="flex items-center gap-1.5 text-[10px] border rounded px-2 py-1">
                        <span className="font-mono font-semibold">{j.container}</span>
                        <span className="text-muted-foreground">{j.type} · {j.status.replace('_', ' ')}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
        {/* Actions */}
        <Button className="w-full h-11" onClick={doAutoDispatch} disabled={autoDispatching || Math.min(availableCount, unassignedJobs.length) === 0}>
          {autoDispatching ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Zap className="mr-2 h-4 w-4" />}
          Auto-Dispatch ({Math.min(availableCount, unassignedJobs.length)} pairs)
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {apiError && <DataErrorBanner error={apiError} entityLabel="调度面板 (Dispatch)" onRetry={fetchData} />}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold">Dispatch Board</h2>
          <div className="flex items-center gap-2 mt-1">
            <Badge className="bg-blue-100 text-blue-700 text-[10px]">
              <Bot className="mr-0.5 h-2.5 w-2.5" /> Jerry: Active
            </Badge>
            <span className="text-xs text-muted-foreground">{drivers.length} drivers · {availableCount} available</span>
          </div>
        </div>
        <div className="flex gap-2">
          <Select value={dateFilter} onValueChange={setDateFilter}>
            <SelectTrigger className="h-8 w-28 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="tomorrow">Tomorrow</SelectItem>
              <SelectItem value="week">This Week</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" className="h-8" onClick={fetchData}>
            <RefreshCcw className="mr-1.5 h-3.5 w-3.5" /> Refresh
          </Button>
          <Button size="sm" className="h-8" onClick={doAutoDispatch} disabled={autoDispatching || Math.min(availableCount, unassignedJobs.length) === 0}>
            {autoDispatching ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Zap className="mr-1.5 h-3.5 w-3.5" />}
            Auto-Dispatch ({Math.min(availableCount, unassignedJobs.length)} pairs)
          </Button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total Drivers', value: drivers.length, color: 'text-foreground' },
          { label: 'Available Now', value: availableCount, color: 'text-green-600' },
          { label: 'On Job', value: onJobCount, color: 'text-blue-600' },
          { label: 'Unassigned Jobs', value: unassignedJobs.length, color: 'text-orange-600' },
        ].map((s) => (
          <Card key={s.label}>
            <CardContent className="pt-4 pb-3 px-4">
              <div className="text-xs text-muted-foreground">{s.label}</div>
              <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">

        {/* Driver Cards */}
        <div className="lg:col-span-3 space-y-3">
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <User className="h-4 w-4 text-primary" /> Driver Status
          </h3>
          {drivers.map((driver) => (
            <Card key={driver.id} className={`border-l-4 ${driver.status === 'available' ? 'border-l-green-400' : driver.status === 'at_port' ? 'border-l-orange-400' : 'border-l-blue-400'}`}>
              <CardContent className="py-3 px-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                      {driver.name.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                    </div>
                    <div>
                      <div className="text-sm font-semibold">{driver.name}</div>
                      <div className="text-[10px] text-muted-foreground font-mono">{driver.plate}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={`text-[10px] px-1.5 ${statusColors[driver.status]}`} variant="outline">
                      {driver.status.replace(/_/g, ' ')}
                    </Badge>
                    <span className="text-[10px] text-muted-foreground">
                      Free: <span className="font-semibold">{driver.eta_free}</span>
                    </span>
                  </div>
                </div>

                {driver.jobs.length > 0 && (
                  <div className="space-y-1.5 mt-2">
                    {driver.jobs.map((job) => (
                      <div key={job.id} className="flex items-center gap-2 text-xs bg-muted/30 rounded px-2 py-1.5">
                        <span className="font-mono text-primary text-[10px] shrink-0">{job.id}</span>
                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold bg-blue-100 text-blue-700`}>{job.type}</span>
                        <span className="font-mono text-[10px] shrink-0">{job.container}</span>
                        <ChevronRight className="h-2.5 w-2.5 text-muted-foreground" />
                        <span className="text-muted-foreground truncate">{job.pickup} → {job.delivery}</span>
                        <span className="ml-auto text-muted-foreground shrink-0">{job.time}</span>
                      </div>
                    ))}
                  </div>
                )}

                {driver.status === 'available' && (
                  <div className="mt-2 flex items-center gap-2">
                    <div className="flex items-center gap-1 text-[10px] text-green-600">
                      <CheckCircle2 className="h-3 w-3" /> Available for new job
                    </div>
                    <Button size="sm" variant="outline" className="h-6 text-[10px] ml-auto">Assign Job</Button>
                  </div>
                )}

                <div className="mt-1.5 flex items-center gap-1 text-[10px] text-muted-foreground">
                  <MapPin className="h-2.5 w-2.5" /> {driver.location}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Right: Unassigned + Street Turns */}
        <div className="lg:col-span-2 space-y-4">

          {/* Unassigned Jobs */}
          <div>
            <h3 className="text-sm font-semibold flex items-center gap-2 mb-3">
              <Package className="h-4 w-4 text-orange-500" />
              Unassigned Jobs
              <Badge variant="secondary" className="text-[10px]">{unassignedJobs.length}</Badge>
            </h3>
            <div className="space-y-3">
              {unassignedJobs.map((job) => (
                <Card key={job.id} className={`border ${priorityConfig[job.priority]}`}>
                  <CardContent className="py-3 px-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="font-mono text-xs font-semibold text-primary">{job.id}</span>
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                        job.priority === 'high' ? 'bg-red-100 text-red-700' :
                        job.priority === 'medium' ? 'bg-orange-100 text-orange-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>{job.priority.toUpperCase()}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-x-2 text-xs mb-2">
                      <div><span className="text-muted-foreground">Type: </span><span className="font-medium">{job.type}</span></div>
                      <div><span className="text-muted-foreground">Size: </span><span className="font-medium">{job.size}</span></div>
                      <div className="col-span-2"><span className="text-muted-foreground">Container: </span><span className="font-mono font-medium">{job.container}</span></div>
                      <div className="col-span-2"><span className="text-muted-foreground">LFD: </span><span className={`font-semibold ${job.priority === 'high' ? 'text-red-600' : ''}`}>{job.lfd}</span></div>
                    </div>
                    <div className="text-[10px] text-muted-foreground mb-2">{job.note}</div>
                    {assignJobId === job.id ? (
                      <div className="flex gap-1 mt-1">
                        <select
                          value={assignDriver}
                          onChange={(e) => setAssignDriver(e.target.value)}
                          className="flex-1 text-xs h-7 border rounded px-1 bg-background"
                        >
                          <option value="">Pick driver…</option>
                          {drivers.map((d) => (
                            <option key={d.id} value={d.name}>{d.name}{d.plate ? ` (${d.plate})` : ''}</option>
                          ))}
                        </select>
                        <Button size="sm" className="h-7 text-xs px-2" disabled={!assignDriver || assigning}
                          onClick={doAssignDriver}>
                          {assigning ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs px-2"
                          onClick={() => { setAssignJobId(null); setAssignJobRawId(null); setAssignDriver(''); }}>
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ) : (
                      <Button size="sm" className="w-full h-7 text-xs"
                        onClick={() => { setAssignJobId(job.id); setAssignJobRawId(job.rawId || job.id); setAssignDriver(''); }}>
                        <UserCheck className="h-3 w-3 mr-1" /> Assign Driver
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Street Turn Opportunities */}
          <div>
            <h3 className="text-sm font-semibold flex items-center gap-2 mb-3">
              <Zap className="h-4 w-4 text-teal-500" />
              Street Turn Opportunities
              <Badge className="bg-teal-100 text-teal-700 text-[10px]">{streetTurns.length} match</Badge>
            </h3>
            {streetTurns.map((st, i) => (
              <Card key={i} className="border-teal-200 bg-teal-50/30">
                <CardContent className="py-3 px-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs font-semibold text-teal-700">Match Score: {st.match_score}%</span>
                    <span className="text-xs font-bold text-green-700 bg-green-100 px-2 py-0.5 rounded">Saves {st.savings}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs mb-1.5">
                    <span className="font-mono">{st.job_a}</span>
                    <ChevronRight className="h-3 w-3 text-teal-500" />
                    <span className="font-mono">{st.job_b}</span>
                  </div>
                  <div className="text-[10px] text-muted-foreground mb-2">{st.note}</div>
                  <div className="text-[10px] text-muted-foreground mb-2">Driver: <span className="font-medium">{st.driver}</span></div>
                  <div className="flex gap-2">
                    <Button size="sm" className="flex-1 h-7 text-xs bg-teal-600 hover:bg-teal-700">Approve</Button>
                    <Button size="sm" variant="outline" className="flex-1 h-7 text-xs">Reject</Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
